import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, Alert, Pressable, Share, StyleSheet, TouchableOpacity, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StatusBar } from 'expo-status-bar'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { Drawer } from 'react-native-drawer-layout'
import { IconButton } from 'react-native-paper'
import { DS } from '@/design-system'
import { useCheatSheet } from '@/components/CheatSheet/useCheatSheet'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { DiamondCountDisplay } from '@/components/Diamonds/Diamonds'
import { getTournamentMaxReentries } from '@ppwr/convex/shared/tournamentConstants'

import { useTournamentLobbyNavigation } from './lobby/useTournamentLobbyNavigation'
import { useAutoSeatOnStart } from './lobby/useAutoSeatOnStart'
import { closeTournament, type LobbyTab } from './mttNavigation'
import { useLobbyChat } from './lobby/useLobbyChat'
import { TournamentLobbyChat, type TournamentMessage } from './lobby/TournamentLobbyChat'
import { TournamentPlayerDrawer } from './lobby/TournamentPlayerDrawer'
import { CohostManagementModal, type CohostTarget } from './lobby/CohostManagementModal'
import { TournamentLobbyActions } from './lobby/TournamentLobbyActions'
import { LobbyCountdown } from './lobby/LobbyCountdown'
import { useMyTournamentTable } from './useMyTournamentTable'
import { useTournamentPresence } from './useTournamentPresence'
import { TournamentLeaderboardView } from './views/TournamentLeaderboardView'
import { TournamentTablesView } from './views/TournamentTablesView'
import { TournamentSummaryView } from './views/TournamentSummaryView'
import { ToggleTabs, type ToggleTabOption } from '../../components/ToggleTabs'

// Stable empty arrays prevent inline `?? []` from allocating a fresh
// reference per render. The fresh reference would invalidate downstream
// `useMemo` caches even when nothing actually changed.
const EMPTY_MESSAGES: TournamentMessage[] = []
const EMPTY_STRINGS: readonly string[] = []

/**
 * Tournament lobby — thin orchestrator over five extracted concerns:
 *  - `useTournamentLobbyNavigation` consolidates the three nav effects.
 *  - `<TournamentLobbyActions />` owns register/start/late-join.
 *  - `<TournamentPlayerDrawer />` owns the player-list + cohost trigger.
 *  - `<CohostManagementModal />` owns add/remove/transfer cohort actions.
 *  - `<TournamentLobbyChat />` owns chat list + input.
 *
 * The previous monolith (~876 lines, 11 mutations, 9 useEffects) was the
 * structural cause of recurring "fix nav race" commits.
 */
const VALID_TABS: readonly LobbyTab[] = ['chat', 'leaderboard', 'tables', 'results'] as const

function coerceTab(raw: string | undefined): LobbyTab {
  return (VALID_TABS as readonly string[]).includes(raw ?? '') ? (raw as LobbyTab) : 'chat'
}

function tabLabel(tab: LobbyTab): string {
  switch (tab) {
    case 'chat':
      return 'Chat'
    case 'leaderboard':
      return 'Leaders'
    case 'tables':
      return 'Tables'
    case 'results':
      return 'Results'
  }
}

export function TournamentLobbyScreen() {
  const {
    tournamentId,
    justFinished,
    justEliminated,
    tab: tabParam
  } = useLocalSearchParams<{
    tournamentId: string
    justFinished?: string
    justEliminated?: string
    tab?: string
  }>()
  const router = useRouter()
  const store = useStore()
  const { user, isAdmin, isSuperAdmin } = store.session
  const { setEvent } = store.analytics
  const userId = user?.auth0Id || ''

  const tournamentIdTyped = tournamentId as Id<'tournaments'> | undefined

  const lobby = useQuery(
    api.tournaments.queries.getTournamentLobby,
    tournamentIdTyped ? { tournamentId: tournamentIdTyped } : 'skip'
  )
  const myTable = useMyTournamentTable(tournamentIdTyped)
  const status = useQuery(
    api.tournaments.queries.getTournamentStatus,
    tournamentIdTyped ? { tournamentId: tournamentIdTyped } : 'skip'
  )
  const messages = useQuery(
    api.tournaments.queries.getTournamentMessages,
    tournamentIdTyped ? { tournamentId: tournamentIdTyped } : 'skip'
  )

  useTournamentPresence(tournamentIdTyped, userId || undefined)

  // Derived role flags. Memoized so downstream `useCallback`s and `useMemo`s
  // that depend on them don't churn from Convex pushes that don't actually
  // change membership / role.
  const registrations = lobby?.registrations
  const cohostUserIds = lobby?.cohostUserIds
  const hostUserId = lobby?.hostUserId
  const isRegistered = useMemo(
    () => !!(userId && registrations?.some((r) => r.userId === userId)),
    [userId, registrations]
  )
  const isWaitlisted = useMemo(
    () => !!(userId && registrations?.some((r) => r.userId === userId && r.waitlisted)),
    [userId, registrations]
  )
  // Config re-entry cap, resolved through the shared helper (falls back to the
  // legacy pre-rename `maxRebuys` field for tournaments created before it).
  const lobbyMaxReentries = useMemo(
    () => (lobby ? getTournamentMaxReentries(lobby.config) : 0),
    [lobby]
  )
  const isHost = !!(userId && hostUserId === userId)
  const isCohost = useMemo(
    () => !!(userId && (cohostUserIds ?? EMPTY_STRINGS).includes(userId)),
    [userId, cohostUserIds]
  )
  const isHostOrCohost = isHost || isCohost
  const canChat = isRegistered || isHostOrCohost
  const isParticipant = isRegistered || isHostOrCohost
  const canViewResults = lobby?.state === 'finished' || myTable?.tournamentEliminated === true

  // ── Analytics: lobby viewed once after data resolves ──────────────────
  // Effect deps deliberately drop the `lobby` object (re-emitted on every
  // Convex push for this tournament, including presence pings) and instead
  // depend on the small set of fields the analytics payload actually reads.
  // The `lobbyViewedRef` guard makes the effect body idempotent even if
  // these deps churn during render.
  const lobbyViewedRef = useRef<string | null>(null)
  const lobbyState = lobby?.state
  const lobbyId = lobby?._id
  const registrationCount = lobby?.registrationCount
  useEffect(() => {
    if (!lobbyId || !tournamentId) return
    if (lobbyViewedRef.current === tournamentId) return
    lobbyViewedRef.current = tournamentId
    setEvent(events.mtt.lobby_screen_viewed, {
      tournamentId: lobbyId,
      state: lobbyState,
      registrationCount,
      isRegistered,
      isHost,
      isCohost
    })
  }, [lobbyId, lobbyState, registrationCount, tournamentId, isRegistered, isHost, isCohost, setEvent])

  // Note: time-dependent UI lives in self-ticking leaf components
  // (`LobbyCountdown`, `LateJoinWindow`, `StartNowGate`, and the
  // leaderboard view's own internal tick when `nowProp` is undefined).
  // This screen never holds a 1Hz state — that previously cascaded
  // through every tab + the drawer + the chat list, defeating
  // memoization everywhere downstream.

  // ── Navigation state machine ──────────────────────────────────────────
  useTournamentLobbyNavigation({ tournamentId, lobby, justFinished })
  useAutoSeatOnStart({
    tournamentId,
    lobbyState,
    // undefined while the query is loading; null only when resolved-unseated.
    gameId: myTable === undefined ? undefined : (myTable?.gameId ?? null),
    tableNumber: myTable?.tableNumber ?? null
  })

  // ── Cohost modal ──────────────────────────────────────────────────────
  const [cohostTarget, setCohostTarget] = useState<CohostTarget | null>(null)
  const [showCohostModal, setShowCohostModal] = useState(false)
  const closeCohostModal = useCallback(() => {
    setShowCohostModal(false)
    setCohostTarget(null)
  }, [])

  // ── Drawer ────────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false)
  const toggleDrawer = useCallback(() => setDrawerOpen((prev) => !prev), [])
  const handleCloseDrawer = useCallback(() => setDrawerOpen(false), [])
  const handleOpenDrawer = useCallback(() => {
    if (isParticipant) setDrawerOpen(true)
  }, [isParticipant])

  // ── Active tab ────────────────────────────────────────────────────────
  // Initial value from `?tab=` query param; subsequent taps update local state.
  // We do NOT re-read the query param on every render (e.g. when a redirect
  // arrives mid-session it should land on the requested tab). Re-running the
  // sync effect on `tabParam` change handles that.
  const [activeTab, setActiveTab] = useState<LobbyTab>(() => coerceTab(tabParam))
  useEffect(() => {
    setActiveTab(coerceTab(tabParam))
  }, [tabParam])

  // Decide which tabs to show based on lobby state. Pre-active states only
  // expose Chat (the other views have nothing to render). Active tournaments
  // expose all four. Finished tournaments hide Tables (every table is
  // finished — the view would show empty/stale data).
  const availableTabs: LobbyTab[] = useMemo(() => {
    if (lobbyState === 'active') {
      return canViewResults ? ['chat', 'leaderboard', 'tables', 'results'] : ['chat', 'leaderboard', 'tables']
    }
    if (lobbyState === 'finished') {
      return canViewResults ? ['chat', 'leaderboard', 'results'] : ['chat', 'leaderboard']
    }
    return ['chat']
  }, [lobbyState, canViewResults])

  // If the requested tab isn't available in the current state, fall back to chat.
  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab('chat')
    }
  }, [availableTabs, activeTab])

  // ── Chat input + send (with retry/restore on error) ──────────────────
  const chat = useLobbyChat({ tournamentId: tournamentIdTyped, userId })

  // ── Share + back ──────────────────────────────────────────────────────
  const handleShare = useCallback(() => {
    if (!lobby) return
    setEvent(events.mtt.share_tapped, { tournamentId: lobby._id })
    Share.share({ message: `Join my tournament "${lobby.name}"! Code: ${lobby.code}` })
  }, [lobby?.name, lobby?.code, lobby?._id, setEvent])

  const handleBack = useCallback(() => {
    closeTournament(router)
  }, [router])

  // ── Admin terminate (escape hatch for wedged active tournaments) ──────
  // UI-gated by `isAdmin`; backend re-checks caller identity but not role
  // (matches the existing lounges convention — see lounges/mutations.ts:708).
  const adminTerminate = useMutation(api.tournaments.mutations.adminTerminateTournament)
  const handleAdminTerminate = useCallback(() => {
    if (!tournamentIdTyped || !userId) return
    Alert.alert(
      'Finish tournament?',
      'This will end the tournament now. A winner will be resolved from current standings and stats will be preserved. This cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Finish',
          style: 'destructive',
          onPress: async () => {
            try {
              await adminTerminate({ tournamentId: tournamentIdTyped, userId })
            } catch (err) {
              Alert.alert('Failed to finish', err instanceof Error ? err.message : 'Unknown error')
            }
          }
        }
      ]
    )
  }, [adminTerminate, tournamentIdTyped, userId])

  const { open: openCheatSheet } = useCheatSheet()

  // ── Cohost target + drawer-content render ─────────────────────────────
  // Both must be declared BEFORE the early-return guards below so the
  // hook order stays stable across renders (Rules of Hooks). On the first
  // render `lobby` is `undefined` and we return early; on the next render
  // it resolves and the rest of the body runs. Any hooks that live after
  // the early returns would only execute on the second render — React
  // throws "Rendered more hooks than during the previous render."
  //
  // `renderDrawerContent` reads from `lobby` so its body has to handle
  // the not-yet-loaded case. The early returns below ensure the drawer
  // is never actually rendered while `lobby` is falsy (the loading /
  // not-found states return their own JSX), so the inner reads are safe
  // in practice — but we still guard defensively against a stale call
  // by short-circuiting to `null`.
  const handleSelectCohostTarget = useCallback((target: CohostTarget) => {
    setCohostTarget(target)
    setShowCohostModal(true)
  }, [])

  const renderDrawerContent = useCallback(() => {
    if (!lobby) return null
    return (
      <TournamentPlayerDrawer
        tournamentId={lobby._id}
        registrations={lobby.registrations}
        registrationCount={lobby.registrationCount}
        hostUserId={lobby.hostUserId}
        cohostUserIds={lobby.cohostUserIds ?? EMPTY_STRINGS}
        currentUserId={userId}
        canManageCohosts={isHost}
        onSelectCohostTarget={handleSelectCohostTarget}
        onClose={handleCloseDrawer}
      />
    )
  }, [
    lobby?._id,
    lobby?.registrations,
    lobby?.registrationCount,
    lobby?.hostUserId,
    lobby?.cohostUserIds,
    userId,
    isHost,
    handleSelectCohostTarget,
    handleCloseDrawer
  ])

  // ── Loading / not-found / cancelled / expired states ──────────────────
  if (lobby === undefined) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar style="dark" />
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack} style={styles.headerBack}>
            <DS.Icon name="Close" size={24} color="Global/Black" />
          </TouchableOpacity>
        </View>
        <View style={styles.centered}>
          <ActivityIndicator size="large" />
        </View>
      </SafeAreaView>
    )
  }

  if (lobby === null) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar style="dark" />
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack} style={styles.headerBack}>
            <DS.Icon name="Close" size={24} color="Global/Black" />
          </TouchableOpacity>
        </View>
        <View style={styles.centered}>
          <DS.Frame gap="12" style={{ padding: 24, alignItems: 'center' }}>
            <DS.Text variant="Heading 3/Medium">Tournament not found</DS.Text>
            <DS.Text variant="Body Large/Regular" style={{ textAlign: 'center', color: DS.palette['Brand/Dark Grey'] }}>
              This tournament may have ended or been removed.
            </DS.Text>
            <DS.Button type="On Light" variant="Medium/Primary" backgroundColor="Brand/Maroon" onPress={handleBack}>
              Back
            </DS.Button>
          </DS.Frame>
        </View>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar style="dark" />
      <View style={styles.header}>
        <TouchableOpacity onPress={handleBack} style={styles.headerBack}>
          <DS.Icon name="Close" size={24} color="Global/Black" />
        </TouchableOpacity>
        <View style={{ flex: 1 }} />
        <View style={styles.headerRight}>
          {isAdmin || isSuperAdmin ? (
            <DiamondCountDisplay textColor={DS.palette['Global/Black']} />
          ) : (
            <TouchableOpacity onPress={openCheatSheet}>
              <DS.Center style={{ flexDirection: 'row', gap: 4 }}>
                <DS.Text variant="Caption/Regular" color="Global/Black">
                  Key
                </DS.Text>
                <DS.Icon name="Key" size={24} color="Global/Black" />
              </DS.Center>
            </TouchableOpacity>
          )}
          {isParticipant && (
            <IconButton
              icon="menu"
              size={24}
              iconColor={DS.palette['Global/Black']}
              onPress={toggleDrawer}
              style={{ backgroundColor: 'transparent' }}
              accessibilityLabel="Open player list"
            />
          )}
        </View>
      </View>
      <Drawer
        open={isParticipant && drawerOpen}
        onOpen={handleOpenDrawer}
        onClose={handleCloseDrawer}
        renderDrawerContent={renderDrawerContent}
        drawerType="front"
        drawerPosition="right"
        drawerStyle={{
          zIndex: 1000,
          backgroundColor: 'transparent',
          width: '100%'
        }}
        swipeEnabled={false}
        overlayStyle={{ backgroundColor: 'rgba(18, 18, 18, .2)' }}
      >
        {/*
          No KeyboardAvoidingView wrapper: it under-shifts content by the
          safe-area top inset AND doesn't account for the iOS autocomplete
          accessory bar. Chat input handles its own keyboard padding via a
          `Keyboard` event listener (see TournamentLobbyChat). This keeps
          the behavior identical on iOS and Android — the listener returns
          `endCoordinates.height` which INCLUDES the accessory bar on iOS.
        */}
        <View style={{ flex: 1 }}>
          {/*
            Tournament info card + actions sit above the tab strip.

            ## Layout per state
            The card collapses to the minimum useful info per state. The tab
            strip below already conveys "Chat / Leaders / Tables / Results"
            so we don't repeat "Tournament in progress" / "Tournament
            complete" — they were noise that ate vertical space.

            - registration/lobby: name + code (with Share) + countdown +
              config subtitle. Time-to-start dominates here.
            - active: name + config subtitle. The tab strip + "Go to Table"
              button already say "we're playing". Date dropped (started).
            - finished: name (with inline winner crown) + config subtitle.
              Date dropped (over). Status line dropped (tabs convey it).
            - cancelled/expired: single banner — no other state matters.
          */}
          <View style={styles.headerContent}>
            <View style={styles.webWrapper}>
              <DS.Frame gap="8">
                <DS.Frame gap="6" style={styles.card}>
                  {lobby.state === 'finished' && lobby.winnerName ? (
                    // Title row: name on the left, winner badge pinned to the
                    // top-right of the card.
                    //
                    // Sizing rules:
                    //  - Tournament name wraps freely (flexShrink: 1, no
                    //    numberOfLines cap) so the user always sees the full
                    //    name.
                    //  - Winner badge is capped at ~40% of the row so a long
                    //    winner display name truncates rather than steals
                    //    space from the title or wraps onto extra lines.
                    //  - `alignItems: 'flex-start'` keeps the badge anchored
                    //    to the first line of the title regardless of wraps.
                    <DS.Frame row alignItems="flex-start" justifyContent="space-between" gap="8">
                      <DS.Text variant="Heading 3/Medium" style={{ flexShrink: 1 }}>
                        {lobby.name}
                      </DS.Text>
                      <DS.Frame
                        row
                        alignItems="center"
                        gap="4"
                        style={{ marginLeft: 'auto', maxWidth: '40%', flexShrink: 0 }}
                      >
                        <DS.Icon name="Crown" color="Brand/Orange" size={16} />
                        <DS.Text
                          variant="Body Small/Medium"
                          numberOfLines={1}
                          ellipsizeMode="tail"
                          style={{ flexShrink: 1 }}
                        >
                          {lobby.winnerName}
                        </DS.Text>
                      </DS.Frame>
                    </DS.Frame>
                  ) : (
                    <DS.Frame row alignItems="center" justifyContent="space-between" gap="8">
                      <DS.Text variant="Heading 3/Medium" style={{ flexShrink: 1 }}>
                        {lobby.name}
                      </DS.Text>
                      {isAdmin && lobby.state === 'active' && (
                        <Pressable
                          onPress={handleAdminTerminate}
                          accessibilityRole="button"
                          accessibilityLabel="Terminate tournament (admin)"
                          style={{ flexShrink: 0 }}
                        >
                          <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Utility/Error'] }}>
                            Terminate
                          </DS.Text>
                        </Pressable>
                      )}
                    </DS.Frame>
                  )}

                  {lobby.state === 'registration' && (
                    <>
                      <DS.Frame row gap="8" style={{ alignItems: 'center' }}>
                        <DS.Text variant="Body Large/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
                          Code: {lobby.code}
                        </DS.Text>
                        <Pressable
                          onPress={handleShare}
                          accessibilityRole="button"
                          accessibilityLabel="Share tournament code"
                        >
                          <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Maroon'] }}>
                            Share
                          </DS.Text>
                        </Pressable>
                      </DS.Frame>

                      <LobbyCountdown scheduledStartTime={lobby.scheduledStartTime} />
                    </>
                  )}

                  {lobby.state === 'cancelled' && (
                    <DS.Text variant="Body Large/Medium" style={{ color: DS.palette['Utility/Error'] }}>
                      Tournament cancelled by host
                    </DS.Text>
                  )}

                  {lobby.state === 'expired' && (
                    <DS.Text variant="Body Large/Medium" style={{ color: DS.palette['Utility/Error'] }}>
                      Tournament expired before it started
                    </DS.Text>
                  )}

                  <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
                    {lobby.config.startingChips.toLocaleString()} chips
                    {' · '}Blinds every {Math.round(lobby.config.blindIncreaseIntervalMs / 60000)} min
                    {' · '}Max {lobby.config.maxPlayersPerTable}/table
                    {lobbyMaxReentries > 0
                      ? ` · ${lobbyMaxReentries} re-entr${lobbyMaxReentries === 1 ? 'y' : 'ies'}`
                      : ''}
                  </DS.Text>
                </DS.Frame>

                <TournamentLobbyActions
                  lobby={lobby}
                  myTable={myTable}
                  status={status ?? null}
                  userId={userId}
                  userName={user?.name || 'Player'}
                  isRegistered={isRegistered}
                  isWaitlisted={isWaitlisted}
                  isHostOrCohost={isHostOrCohost}
                />
              </DS.Frame>
            </View>
          </View>

          {/*
            Tabs only render once the tournament is active or finished. Pre-active
            states show Chat directly without the tab strip; ToggleTabs already
            no-ops for a single option, but we also skip the wrapper padding.
          */}
          {availableTabs.length > 1 && (
            <View style={styles.tabBarWrapper}>
              <ToggleTabs<LobbyTab>
                options={availableTabs.map<ToggleTabOption<LobbyTab>>((tab) => ({
                  value: tab,
                  label: tabLabel(tab)
                }))}
                value={activeTab}
                onChange={setActiveTab}
              />
            </View>
          )}

          {/* Tab content */}
          <View style={{ flex: 1 }}>
            {activeTab === 'chat' && (
              <TournamentLobbyChat
                messages={(messages as TournamentMessage[] | undefined) ?? EMPTY_MESSAGES}
                currentUserId={userId}
                canChat={canChat}
                onSend={chat.send}
                text={chat.text}
                onChangeText={chat.setText}
                pending={chat.pending}
              />
            )}
            {activeTab === 'leaderboard' && tournamentIdTyped && (
              <TournamentLeaderboardView
                tournamentId={tournamentIdTyped}
                justEliminated={justEliminated === '1'}
                justFinished={justFinished === '1'}
                // Pass `status` so the leaderboard reuses the lobby's
                // subscription. Don't pass `now` — the leaderboard owns
                // its own fallback ticker so the lobby tree doesn't have
                // to hold a 1Hz state at the root just to feed it.
                status={status}
              />
            )}
            {activeTab === 'tables' && tournamentIdTyped && <TournamentTablesView tournamentId={tournamentIdTyped} />}
            {activeTab === 'results' && tournamentIdTyped && (
              <TournamentSummaryView
                tournamentId={tournamentIdTyped}
                tournamentState={lobby.state}
                isEliminated={myTable?.tournamentEliminated === true}
              />
            )}
          </View>
        </View>
      </Drawer>

      <CohostManagementModal
        visible={showCohostModal}
        onClose={closeCohostModal}
        target={cohostTarget}
        tournamentId={tournamentIdTyped as Id<'tournaments'>}
        hostUserId={userId}
      />
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  webWrapper: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center' as const
  },
  card: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 10
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    height: 54
  },
  headerBack: {
    padding: 8
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  headerContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 8
  },
  // Snug spacing: card → tabs gap = headerContent.paddingBottom (8) only.
  // Previously this wrapper added another 16px above the tabs which read
  // as wasted space on small screens.
  tabBarWrapper: {
    paddingTop: 0,
    paddingBottom: 8
  }
})
